/**
 * Impersonation session helpers (sessionStorage-based, tab-scoped).
 *
 * The impersonation tab signs into a separate Firebase app (see
 * firebase/impersonationAuth.ts), then stores its encrypted Firebase ID token
 * in sessionStorage. callAPI sends that token as Authorization: Bearer instead
 * of the cookie that the admin's main session uses, so the two sessions never
 * cross-contaminate even on the same origin.
 */

interface ImpersonationSession {
  token: string;
  createdAt: number;
}

const IMPERSONATION_KEYS = {
  ACTIVE: 'impersonation.active',
  TOKEN: 'impersonation.token',
  CREATED_AT: 'impersonation.createdAt',
} as const;

// Tab marker, set when impersonation starts, never cleared by us. Lives in
// sessionStorage so it dies with the tab. Used to identify a tab as "this
// tab was/is an impersonation tab" even after the active session ended, so
// destructive cleanup paths (e.g. UserRouteGuard's cookie removal) can be
// suppressed — we don't want a dying impersonation tab to wipe the admin's
// cookie, which is shared across tabs of the same origin.
const IMPERSONATION_TAB_MARKER = 'impersonation.tabMarker';

export const markTabAsImpersonation = (): void => {
  try {
    sessionStorage.setItem(IMPERSONATION_TAB_MARKER, '1');
  } catch (error) {
    console.error('Error marking tab as impersonation:', error);
  }
};

export const isImpersonationTab = (): boolean => {
  try {
    return sessionStorage.getItem(IMPERSONATION_TAB_MARKER) === '1';
  } catch (error) {
    return false;
  }
};

const MAX_SESSION_AGE = 24 * 60 * 60 * 1000; // 24 hours

export const isImpersonationSession = (): boolean => {
  try {
    const active = sessionStorage.getItem(IMPERSONATION_KEYS.ACTIVE);
    const token = sessionStorage.getItem(IMPERSONATION_KEYS.TOKEN);
    const createdAt = sessionStorage.getItem(IMPERSONATION_KEYS.CREATED_AT);

    if (!active || !token || !createdAt) {
      return false;
    }

    if (Date.now() - parseInt(createdAt) > MAX_SESSION_AGE) {
      clearImpersonationSession();
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error checking impersonation session:', error);
    return false;
  }
};

export const getImpersonationToken = (): string | null => {
  try {
    if (!isImpersonationSession()) return null;
    return sessionStorage.getItem(IMPERSONATION_KEYS.TOKEN);
  } catch (error) {
    console.error('Error getting impersonation token:', error);
    return null;
  }
};

export const setImpersonationSession = (data: { token: string }): boolean => {
  try {
    sessionStorage.setItem(IMPERSONATION_KEYS.ACTIVE, '1');
    sessionStorage.setItem(IMPERSONATION_KEYS.TOKEN, data.token);
    sessionStorage.setItem(IMPERSONATION_KEYS.CREATED_AT, Date.now().toString());
    // Permanent (within tab lifetime) marker — see markTabAsImpersonation().
    markTabAsImpersonation();
    return true;
  } catch (error) {
    console.error('Error setting impersonation session:', error);
    return false;
  }
};

/**
 * Update the encrypted token in-place (used after a Firebase ID token refresh).
 */
export const updateImpersonationToken = (token: string): void => {
  try {
    if (sessionStorage.getItem(IMPERSONATION_KEYS.ACTIVE)) {
      sessionStorage.setItem(IMPERSONATION_KEYS.TOKEN, token);
    }
  } catch (error) {
    console.error('Error updating impersonation token:', error);
  }
};

export const clearImpersonationSession = (): boolean => {
  try {
    Object.values(IMPERSONATION_KEYS).forEach((key) => sessionStorage.removeItem(key));
    return true;
  } catch (error) {
    console.error('Error clearing impersonation session:', error);
    return false;
  }
};

export const shouldUseImpersonationAuth = (): boolean => isImpersonationSession();

export const getImpersonationDisplayInfo = (): { isImpersonating: boolean; sessionAge?: string } => {
  if (!isImpersonationSession()) {
    return { isImpersonating: false };
  }

  const createdAt = parseInt(sessionStorage.getItem(IMPERSONATION_KEYS.CREATED_AT) || '0');
  const ageInMinutes = Math.floor((Date.now() - createdAt) / (60 * 1000));

  let sessionAge: string;
  if (ageInMinutes < 1) {
    sessionAge = 'Just now';
  } else if (ageInMinutes < 60) {
    sessionAge = `${ageInMinutes} minute${ageInMinutes === 1 ? '' : 's'} ago`;
  } else {
    const ageInHours = Math.floor(ageInMinutes / 60);
    sessionAge = `${ageInHours} hour${ageInHours === 1 ? '' : 's'} ago`;
  }

  return { isImpersonating: true, sessionAge };
};
